import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BookOpen, 
  Users, 
  Megaphone, 
  Wallet,
  FileText,
  Settings,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  GraduationCap,
  Sparkles,
  UserCheck,
  Mail,
  Receipt,
  Calculator,
  PiggyBank,
  FileSpreadsheet,
  Newspaper,
  FolderOpen,
  Cog,
  CreditCard,
  Target,
  Contact2,
  Flower2,
  Mountain
} from 'lucide-react';

const AdminLayout = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['cursos', 'alumnos', 'marketing', 'finanzas']);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const isActive = (path) => location.pathname === path;
  const isParentActive = (paths) => paths.some(p => location.pathname.startsWith(p));

  // Navigation structure - ordered by conscious business flow
  const navigation = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin',
      description: 'Visión clara'
    },
    {
      id: 'campo',
      label: 'El Campo',
      icon: ({ className }) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
          <circle cx="12" cy="12" r="9"/>
          <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none"/>
        </svg>
      ),
      path: '/admin/campo',
      description: 'Corazón vivo'
    },
    {
      id: 'finanzas',
      label: 'Finanzas',
      icon: Wallet,
      description: 'Abundancia y fiscalidad',
      children: [
        { label: 'Centro financiero', path: '/admin/finanzas', icon: PiggyBank },
      ]
    },
    {
      id: 'alumnos',
      label: 'Alumnos',
      icon: Users,
      description: 'Tu comunidad',
      children: [
        { label: 'Todos los alumnos', path: '/admin/alumnos', icon: Users },
        { label: 'Detalle alumno', path: '/admin/alumnos/detalle', icon: UserCheck, hidden: true },
      ]
    },
    {
      id: 'cursos',
      label: 'Cursos',
      icon: BookOpen,
      description: 'Gestión de producto',
      children: [
        { label: 'Vista general', path: '/admin/cursos', icon: GraduationCap },
        { label: 'Cursos grabados', path: '/admin/cursos/grabados', icon: BookOpen },
        { label: 'Formaciones en vivo', path: '/admin/formaciones-especiales', icon: Sparkles },
        { label: 'Masterclasses', path: '/admin/masterclass', icon: Sparkles },
        { label: 'Talleres de pago', path: '/admin/paid-workshops', icon: Sparkles },
        { label: 'Retiros', path: '/admin/retiros', icon: Mountain },
      ]
    },
    {
      id: 'campus',
      label: 'Campus',
      icon: Flower2,
      description: 'Campus de Interioridad',
      path: '/admin/campus',
    },
    {
      id: 'marketing',
      label: 'Marketing',
      icon: Megaphone,
      description: 'Embudos y crecimiento',
      children: [
        { label: 'CRM 360°', path: '/admin/crm', icon: Contact2 },
        { label: 'Panel de crecimiento', path: '/admin/marketing', icon: Megaphone },
      ]
    },
    {
      id: 'contenidos',
      label: 'Contenidos',
      icon: FileText,
      description: 'Blog y recursos',
      children: [
        { label: 'Publicaciones', path: '/admin/contenidos', icon: Newspaper },
        { label: 'Recursos', path: '/admin/contenidos/recursos', icon: FolderOpen },
        { label: 'Mensajes', path: '/admin/mensajes', icon: Mail },
      ]
    },
    {
      id: 'configuracion',
      label: 'Configuración',
      icon: Settings,
      description: 'Ajustes del sistema',
      children: [
        { label: 'General', path: '/admin/configuracion', icon: Cog },
        { label: 'Datos fiscales', path: '/admin/configuracion/fiscal', icon: Calculator },
        { label: 'Conexiones', path: '/admin/configuracion/conexiones', icon: CreditCard },
        { label: 'Mi perfil', path: '/admin/perfil', icon: UserCheck },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar toggle */}
      <button 
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-20 left-4 z-50 lg:hidden bg-white p-2 rounded-lg shadow-lg border border-slate-200"
        aria-label="Toggle menu"
      >
        {sidebarOpen ? <X className="w-5 h-5 text-slate-600" /> : <Menu className="w-5 h-5 text-slate-600" />}
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-[72px] h-[calc(100vh-72px)] w-64 bg-white border-r border-slate-200 z-40 transition-transform duration-300 overflow-y-auto ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="p-4">
          {/* Header */}
          <div className="mb-6 pb-4 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Panel Admin</p>
            <p className="text-sm text-slate-500 mt-1">Escuela Akáshica</p>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {navigation.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedMenus.includes(item.id);
              const isItemActive = item.path ? isActive(item.path) : isParentActive(item.children?.map(c => c.path) || []);
              
              return (
                <div key={item.id}>
                  {/* Parent item */}
                  {hasChildren ? (
                    <button
                      onClick={() => toggleMenu(item.id)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                        isItemActive 
                          ? 'bg-slate-100 text-slate-900' 
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className={`w-5 h-5 ${isItemActive ? 'text-indigo-600' : 'text-slate-400'}`} strokeWidth={1.5} />
                        <div className="text-left">
                          <span className="font-medium block">{item.label}</span>
                          <span className="text-xs text-slate-400">{item.description}</span>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        isItemActive 
                          ? 'bg-indigo-600 text-white' 
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <item.icon className={`w-5 h-5 ${isItemActive ? 'text-white' : 'text-slate-400'}`} strokeWidth={1.5} />
                      <div>
                        <span className="font-medium block">{item.label}</span>
                        <span className={`text-xs ${isItemActive ? 'text-indigo-200' : 'text-slate-400'}`}>{item.description}</span>
                      </div>
                    </Link>
                  )}

                  {/* Children */}
                  {hasChildren && isExpanded && (
                    <div className="ml-4 mt-1 space-y-1 border-l border-slate-100 pl-3">
                      {item.children.filter(child => !child.hidden).map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                            isActive(child.path)
                              ? 'bg-indigo-50 text-indigo-700 font-medium'
                              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                          }`}
                        >
                          <child.icon className={`w-4 h-4 ${isActive(child.path) ? 'text-indigo-600' : 'text-slate-400'}`} strokeWidth={1.5} />
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="mt-8 pt-4 border-t border-slate-100">
            <div className="px-3 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-semibold text-indigo-700">Objetivo 2026</span>
              </div>
              <p className="text-lg font-bold text-indigo-900">120.000 €</p>
              <p className="text-xs text-indigo-600">Modelo autónomo 30h/sem</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-[72px] min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
